/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import _ from 'lodash';
import { values } from 'mobx';
import { types } from 'mobx-state-tree';
import { consolidateToMap } from '@amzn/base-ui/dist/helpers/utils';
import { BaseStore } from '@amzn/base-ui/dist/models/BaseStore';

import { getAnnouncements, createAnnouncement, updateAnnouncement, deleteAnnouncement } from '../../helpers/api';
import { Announcement } from './Announcement';

// ==================================================================
// AnnouncementsStore
// ==================================================================
const AnnouncementsStore = BaseStore.named('AnnouncementsStore')
  .props({
    announcements: types.optional(types.map(Announcement), {}),
    tickPeriod: 30 * 1000, // 30 seconds
  })

  .actions(self => {
    const superCleanup = self.cleanup;

    return {
      async doLoad() {
        const announcements = await getAnnouncements();
        self.runInAction(() => {
          consolidateToMap(self.announcements, announcements, (existing, newItem) => {
            existing.setAnnouncement(newItem);
          });
        });
      },

      addAnnouncement(raw) {
        const id = raw.id;
        const previous = self.announcements.get(id);
        if (!previous) {
          self.announcements.put(raw);
        } else {
          previous.setAnnouncement(raw);
        }
      },

      async createAnnouncement(data) {
        const result = await createAnnouncement(data);
        self.addAnnouncement(result);
        return self.getAnnouncement(result.id);
      },

      async updateAnnouncement(id, data) {
        const result = await updateAnnouncement(id, data);
        self.addAnnouncement(result);
        return self.getAnnouncement(result.id);
      },

      async deleteAnnouncement(id, rev) {
        await deleteAnnouncement(id, rev);
        self.runInAction(() => {
          self.announcements.delete(id);
        });
      },

      cleanup: () => {
        self.announcements.clear();
        superCleanup();
      },
    };
  })

  .views(self => ({
    get empty() {
      return self.announcements.size === 0;
    },

    get total() {
      return self.announcements.size;
    },

    get list() {
      return _.orderBy(values(self.announcements), ['priority', 'createdAt'], ['asc', 'desc']);
    },

    getAnnouncement(id) {
      return self.announcements.get(id);
    },
  }));

function registerContextItems(appContext) {
  appContext.announcementsStore = AnnouncementsStore.create({}, appContext);
}

export { AnnouncementsStore, registerContextItems };
